import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Button,
  Drawer,
  Input,
  notification,
  Pagination,
  Select,
  Spin,
} from 'antd';
import { collection, orderBy, query } from 'firebase/firestore';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import { ResponsiveTable } from '@src/components/Table';
import type { ResponsiveTableColumns } from '@src/components/Table';
import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type { IWorkJourneyReplacementCandidate } from '@src/models/interfaces/workJourneyManagement.interface';
import { useFirestoreRealtimeBatched } from '@src/packages/utils';
import {
  formatAttendanceIdentifierDisplay,
  getOrganizationCountry,
  needsJurisdictionIdentifier,
  type OrganizationCountrySource,
} from '@src/packages/utils/nationalId';
import JurisdictionIdPromptModal from '@src/packages/workJourney/components/JurisdictionIdPromptModal';
import { persistOrganizationUserJurisdictionId } from '@src/packages/workJourney/utils/persistOrganizationUserJurisdictionId';
import UserRepository from '@src/repositories/UserRepository';
import WorkJourneyManagementService from '@src/services/WorkJourneyManagementService';
import IntlMessages from '@src/util/IntlMessages';

import styles from '../styles.module.less';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

type UserJobOption = { id: string; name: string };

type Props = {
  open: boolean;
  organizationId?: string;
  excludeUserIds: string[];
  onClose: () => void;
  onAdd: (candidate: IWorkJourneyReplacementCandidate) => void | Promise<void>;
};

export const PlanMonthlyJourneyAddUsersDrawer: FC<Props> = ({
  open,
  organizationId,
  excludeUserIds,
  onClose,
  onAdd,
}) => {
  const intl = useIntl();
  const serviceRef = useRef(new WorkJourneyManagementService());
  const userRepositoryRef = useRef(new UserRepository());
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [jobId, setJobId] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<IWorkJourneyReplacementCandidate[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pendingJurisdictionUser, setPendingJurisdictionUser] =
    useState<IWorkJourneyReplacementCandidate | null>(null);

  const { orgId, organizationCountry } = useSelector(
    ({
      organizations,
    }: {
      organizations: {
        organization: OrganizationCountrySource & { id?: string };
      };
    }) => ({
      orgId: organizations.organization?.id,
      organizationCountry: getOrganizationCountry(organizations.organization),
    }),
  );
  const effectiveOrgId = organizationId || orgId;

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebouncedSearch('');
      setJobId(undefined);
      setPage(1);
      setUsers([]);
      setTotal(0);
      setPendingJurisdictionUser(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !effectiveOrgId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await serviceRef.current.listReplacementCandidates({
          organizationId: effectiveOrgId,
          search: debouncedSearch || undefined,
          jobId,
          excludeUserIds,
          from: (page - 1) * PAGE_SIZE,
          pageSize: PAGE_SIZE,
        });
        if (cancelled) return;
        setUsers(response.users);
        setTotal(response.total);
      } catch (error) {
        console.error('Error loading plan add-users candidates:', error);
        if (!cancelled) {
          setUsers([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, effectiveOrgId, debouncedSearch, jobId, page, excludeUserIds]);

  const jobsQueryList = useMemo(() => {
    if (!effectiveOrgId || !open) return [];
    return [
      query(
        collection(db, FSCollections.ORGANIZATIONS, effectiveOrgId, FSCollections.USER_JOBS),
        orderBy('name'),
      ),
    ];
  }, [effectiveOrgId, open]);

  const { data: jobsData = [] } = useFirestoreRealtimeBatched(jobsQueryList, [
    effectiveOrgId,
    open,
  ]) as {
    data?: UserJobOption[];
  };

  const jobOptions = useMemo(
    () =>
      (jobsData || []).map(job => ({
        value: job.id,
        label: job.name,
      })),
    [jobsData],
  );

  const handleAddCandidate = useCallback(
    async (candidate: IWorkJourneyReplacementCandidate) => {
      if (!effectiveOrgId) {
        return;
      }
      try {
        const orgUser = await userRepositoryRef.current.getUser(
          effectiveOrgId,
          candidate.id,
        );
        if (!orgUser) {
          throw new Error('Organization user not found');
        }
        const personalIdCard = orgUser.idCard || candidate.idCard || '';
        const jurisdictionId =
          orgUser.jurisdictionId ?? candidate.jurisdictionId;
        const personalCountry = orgUser.address?.country ?? null;
        const resolvedCandidate = {
          ...candidate,
          idCard: personalIdCard,
          jurisdictionId,
          address: orgUser.address ?? candidate.address,
        };
        if (
          needsJurisdictionIdentifier(
            organizationCountry,
            personalIdCard,
            jurisdictionId,
            personalCountry,
          )
        ) {
          setPendingJurisdictionUser(resolvedCandidate);
          return;
        }
        await onAdd(resolvedCandidate);
      } catch (error) {
        console.error('Error loading organization user:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.loading.options.error' }),
        });
      }
    },
    [effectiveOrgId, intl, onAdd, organizationCountry],
  );

  const columns: ResponsiveTableColumns<IWorkJourneyReplacementCandidate> = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'workJourney.management.plan.columns.user' }),
        key: 'user',
        render: (record: IWorkJourneyReplacementCandidate) => (
          <div className={styles.userCell}>
            <MemberAvatar
              size={32}
              user={{ displayName: record.displayName, photoURL: record.photoURL ?? undefined }}
            />
            <span>{record.displayName}</span>
          </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'workJourney.management.plan.columns.idCard' }),
        key: 'idCard',
        render: (record: IWorkJourneyReplacementCandidate) =>
          formatAttendanceIdentifierDisplay(organizationCountry, record) || '—',
      },
      {
        title: intl.formatMessage({ id: 'workJourney.management.plan.columns.job' }),
        key: 'job',
        render: (record: IWorkJourneyReplacementCandidate) => record.job?.name || '—',
      },
      {
        title: intl.formatMessage({ id: 'workJourney.management.plan.columns.journey' }),
        key: 'journey',
        render: (record: IWorkJourneyReplacementCandidate) =>
          record.currentWorkJourney?.workJourneyConfigName ||
          intl.formatMessage({ id: 'workJourney.management.plan.noJourney' }),
      },
      {
        title: '',
        key: 'action',
        width: 56,
        render: (record: IWorkJourneyReplacementCandidate) => (
          <Button
            type="primary"
            shape="circle"
            icon={<PlusOutlined />}
            onClick={() => {
              void handleAddCandidate(record);
            }}
            aria-label={intl.formatMessage({ id: 'workJourney.management.plan.addUser' })}
          />
        ),
      },
    ],
    [handleAddCandidate, intl, organizationCountry],
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      width={720}
      destroyOnClose
      title={null}
      closable={false}
      footer={null}
      className={styles.drawer}
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer
            title={
              <Title.Header
                value={intl.formatMessage({ id: 'workJourney.management.plan.addUsers.title' })}
              />
            }
            goBack={handleClose}
            actionButtons={[
              {
                label: intl.formatMessage({ id: 'general.save' }),
                type: 'primary' as const,
                action: handleClose,
              },
            ]}
          />
        </BoxContainer>
        <BoxContainer content>
          <p>
            <IntlMessages id="workJourney.management.plan.addUsers.hint" />
          </p>
          <div className={styles.filtersRow}>
            <Input.Search
              allowClear
              value={search}
              onChange={event => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder={intl.formatMessage({
                id: 'workJourney.management.plan.searchPlaceholder',
              })}
              className={`${styles.searchInput} gx-mb-0`}
            />
            <Select
              allowClear
              value={jobId}
              onChange={value => {
                setJobId(value);
                setPage(1);
              }}
              options={jobOptions}
              placeholder={intl.formatMessage({
                id: 'workJourney.management.plan.jobPlaceholder',
              })}
              className={styles.jobSelect}
            />
          </div>
          <Spin spinning={loading}>
            <ResponsiveTable columns={columns} dataSource={users} loading={loading} />
          </Spin>
          {total > PAGE_SIZE && (
            <div className={styles.pagination}>
              <Pagination
                current={page}
                pageSize={PAGE_SIZE}
                total={total}
                onChange={setPage}
                showSizeChanger={false}
              />
            </div>
          )}
        </BoxContainer>
      </BoxContainer>
      <JurisdictionIdPromptModal
        open={!!pendingJurisdictionUser}
        organizationCountry={organizationCountry}
        personalCountry={pendingJurisdictionUser?.address?.country}
        personalIdCard={pendingJurisdictionUser?.idCard}
        userName={pendingJurisdictionUser?.displayName}
        onCancel={() => setPendingJurisdictionUser(null)}
        onConfirm={async jurisdictionId => {
          if (!effectiveOrgId || !pendingJurisdictionUser) {
            return;
          }
          try {
            const orgUser = await userRepositoryRef.current.getUser(
              effectiveOrgId,
              pendingJurisdictionUser.id,
            );
            if (!orgUser) {
              throw new Error('Organization user not found');
            }
            await persistOrganizationUserJurisdictionId(
              effectiveOrgId,
              orgUser,
              jurisdictionId,
            );
            await onAdd({ ...pendingJurisdictionUser, jurisdictionId });
            setPendingJurisdictionUser(null);
          } catch (error) {
            console.error('Error saving jurisdiction identifier:', error);
            notification.error({
              message: intl.formatMessage({ id: 'general.save.error.message' }),
            });
          }
        }}
      />
    </Drawer>
  );
};
